<template>
  <div>
  <AppModal
    :open="open"
    :title="$t('app.reservationDetailTitle')"
    :description="$t('app.reservationDetailTitle')"
    test-id="reservation-detail-modal"
    @close="emit('close')"
  >
    <div v-if="reservation" class="min-w-0">
      <!-- Edit form (when editing) -->
      <div v-if="editing" class="space-y-4 mb-4 p-4 rounded-xl bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-slate-700">
        <h4 class="text-sm font-semibold text-charcoal dark:text-white">{{ $t('app.reservationsEditDateTime') }}</h4>
        <div class="grid gap-3">
          <div>
            <label for="res-edit-date" class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{{ $t('app.reservationsTableDate') }}</label>
            <input
              id="res-edit-date"
              v-model="editForm.booking_date"
              type="date"
              class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
              :min="todayYmd"
              aria-describedby="res-edit-date-error"
            >
            <p id="res-edit-date-error" v-if="editError" class="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{{ editError }}</p>
          </div>
          <div>
            <label for="res-edit-time" class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{{ $t('app.reservationsTableTime') }}</label>
            <input
              id="res-edit-time"
              v-model="editForm.booking_time"
              type="time"
              class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
            >
          </div>
          <div>
            <label for="res-edit-pax" class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{{ $t('app.reservationsTablePax') }}</label>
            <input
              id="res-edit-pax"
              v-model.number="editForm.pax"
              type="number"
              min="1"
              max="99"
              class="w-full min-h-[44px] rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-zinc-800 px-3 py-2 text-charcoal dark:text-white"
            >
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <AppButton variant="primary" class="min-h-[44px]" :disabled="updating" @click="saveEdit">
            {{ updating ? $t('app.loading') : $t('app.save') }}
          </AppButton>
          <AppButton variant="secondary" class="min-h-[44px]" :disabled="updating" @click="editing = false">
            {{ $t('app.cancel') }}
          </AppButton>
        </div>
      </div>

      <dl class="space-y-3 text-sm">
        <div>
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationsTableDate') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">{{ formatDisplayDate(reservation.booking_date) || reservation.booking_date }}</dd>
        </div>
        <div>
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationsTableTime') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">{{ reservation.booking_time }}</dd>
        </div>
        <div>
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationsTablePax') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">{{ reservation.pax }}</dd>
        </div>
        <div>
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationsTableCustomer') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">{{ reservation.customer_name || '—' }}</dd>
        </div>
        <div>
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationsTableMobile') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">
            <a
              v-if="reservation.customer_mobile"
              :href="`tel:${reservation.customer_mobile}`"
              class="text-primary hover:underline"
            >
              {{ reservation.customer_mobile }}
            </a>
            <span v-else>—</span>
          </dd>
        </div>
        <div>
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationsTableStatus') }}</dt>
          <dd class="mt-0.5">
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium min-h-[28px]"
              :class="statusClass(reservation.status)"
            >
              {{ statusLabel(reservation.status) }}
            </span>
          </dd>
        </div>
        <div v-if="reservation.created_at">
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationDetailCreatedAt') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">{{ formatDateTime(reservation.created_at) }}</dd>
        </div>
        <div v-if="reservation.reminder_sent_at">
          <dt class="font-medium text-slate-500 dark:text-slate-400">{{ $t('app.reservationDetailReminderSent') }}</dt>
          <dd class="mt-0.5 text-charcoal dark:text-white">{{ formatDateTime(reservation.reminder_sent_at) }}</dd>
        </div>
      </dl>
    </div>

    <template #footer>
      <div class="w-full flex flex-col gap-3">
        <template v-if="reservation && reservation.status === 'pending'">
          <AppButton
            variant="primary"
            class="min-h-[44px] w-full"
            :aria-label="$t('app.reservationsApprove')"
            :disabled="updating"
            @click="confirmAction = 'approve'"
          >
            {{ $t('app.reservationsApprove') }}
          </AppButton>
          <AppButton
            variant="secondary"
            class="min-h-[44px] w-full border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
            :aria-label="$t('app.reservationsReject')"
            :disabled="updating"
            @click="confirmAction = 'reject'"
          >
            {{ $t('app.reservationsReject') }}
          </AppButton>
        </template>
        <AppButton
          v-if="reservation && canCancel"
          variant="secondary"
          class="min-h-[44px] w-full border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
          :aria-label="$t('app.reservationsRemove')"
          :disabled="updating"
          @click="$emit('cancel')"
        >
          {{ $t('app.reservationsRemove') }}
        </AppButton>
        <AppButton
          v-if="reservation && reservation.status !== 'cancelled' && !editing"
          variant="secondary"
          class="min-h-[44px] w-full"
          :aria-label="$t('app.reservationsEdit')"
          :disabled="updating"
          @click="editing = true"
        >
          {{ $t('app.reservationsEdit') }}
        </AppButton>
      </div>
    </template>
  </AppModal>

  <!-- Confirm Approve / Reject -->
  <AppModal
    :open="confirmAction !== null"
    :title="confirmAction === 'approve' ? $t('app.reservationApproveConfirmTitle') : $t('app.reservationRejectConfirmTitle')"
    :description="confirmAction === 'approve' ? $t('app.reservationApproveConfirmMessage') : $t('app.reservationRejectConfirmMessage')"
    test-id="reservation-confirm-modal"
    @close="confirmAction = null"
  >
    <p class="text-sm text-slate-600 dark:text-slate-400">
      {{ confirmAction === 'approve' ? $t('app.reservationApproveConfirmMessage') : $t('app.reservationRejectConfirmMessage') }}
    </p>
    <template #footer>
      <div class="flex flex-wrap gap-2 justify-end">
        <AppButton variant="secondary" class="min-h-[44px]" @click="confirmAction = null">
          {{ $t('app.cancel') }}
        </AppButton>
        <AppButton
          v-if="confirmAction === 'approve'"
          variant="primary"
          class="min-h-[44px]"
          :disabled="updating"
          @click="confirmAction = null; emit('approve')"
        >
          {{ $t('app.confirm') }}
        </AppButton>
        <AppButton
          v-if="confirmAction === 'reject'"
          variant="secondary"
          class="min-h-[44px] border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
          :disabled="updating"
          @click="confirmAction = null; emit('reject')"
        >
          {{ $t('app.confirm') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppModal from '@/components/ui/AppModal.vue'
import AppButton from '@/components/ui/AppButton.vue'
import { formatDisplayDate, formatDisplayDateTime, todayYmd as getTodayYmd } from '@/utils/date'

const { t } = useI18n()

const props = defineProps({
  open: { type: Boolean, default: false },
  reservation: { type: Object, default: null },
  updating: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'approve', 'reject', 'cancel', 'update'])

const editing = ref(false)
const editError = ref('')
const editForm = ref({ booking_date: '', booking_time: '', pax: 1 })
/** 'approve' | 'reject' | null when confirmation dialog is open */
const confirmAction = ref(null)

/** Today YYYY-MM-DD (owners can edit to today or any future date). */
const todayYmd = computed(() => getTodayYmd())

const canCancel = computed(() => {
  const s = props.reservation?.status
  return s === 'pending' || s === 'approved'
})

watch(
  () => props.reservation,
  (r) => {
    if (r) {
      editing.value = false
      editError.value = ''
      editForm.value = {
        booking_date: r.booking_date ?? '',
        booking_time: r.booking_time ?? '',
        pax: r.pax ?? 1,
      }
    }
  },
  { immediate: true }
)

function saveEdit() {
  editError.value = ''
  const date = editForm.value.booking_date?.trim()
  const time = editForm.value.booking_time?.trim()
  const pax = editForm.value.pax
  if (!date || !time) {
    editError.value = t('app.reservationsTableDate') + ' / ' + t('app.reservationsTableTime') + ' required.'
    return
  }
  if (date < todayYmd.value) {
    editError.value = t('app.reservationsTableDate') + ' must be today or a future date.'
    return
  }
  const paxNum = Number(pax)
  if (Number.isNaN(paxNum) || paxNum < 1 || paxNum > 99) {
    editError.value = t('app.reservationsTablePax') + ' must be between 1 and 99.'
    return
  }
  editing.value = false
  emit('update', {
    booking_date: date,
    booking_time: time.length === 5 ? time : time.slice(0, 5),
    pax: paxNum,
  })
}

function statusLabel(status) {
  const key = `app.reservationStatus_${status}`
  const val = t(key)
  return val !== key ? val : status
}

function statusClass(status) {
  switch (status) {
    case 'pending':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
    case 'approved':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
    case 'rejected':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
    case 'cancelled':
      return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
    default:
      return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
  }
}

function formatDateTime(iso) {
  if (!iso) return '—'
  const formatted = formatDisplayDateTime(iso)
  return formatted || iso
}
</script>
